import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, formatApiErrorDetail } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  CheckCircle2,
  Clock3,
  Eye,
  EyeOff,
  Loader2,
  LockKeyhole,
  MapPin,
  Search,
  ShieldCheck,
  UserPlus,
} from "lucide-react";

const COMPANIES_ENDPOINT =
  `${API}/auth/access-request/companies`;

const REQUEST_ENDPOINT =
  `${API}/auth/access-request`;

const CONFIG_ENDPOINT =
  `${API}/auth/access-request/config`;

const DEFAULT_DEADLINE_HOURS = 24;

const REQUESTED_PROFILES = [
  {
    value: "technician",
    label: "Técnico",
  },
  {
    value: "supervisor",
    label: "Supervisor",
  },
  {
    value: "coordinator",
    label: "Coordenador",
  },
  {
    value: "client",
    label: "Cliente",
  },
];

function normalizeCompany(company) {
  if (!company) {
    return null;
  }

  return {
    id: company.id,
    name:
      company.name ||
      company.company_name ||
      company.client_name ||
      "",
    cnpj:
      company.cnpj ||
      "",
    address:
      company.address ||
      "",
    city:
      company.city ||
      "",
    state:
      company.state ||
      "",
    units: Array.isArray(
      company.units,
    )
      ? company.units
      : [],
  };
}

function getUnits(company) {
  if (!company) {
    return [];
  }

  return Array.isArray(
    company.units,
  )
    ? company.units
    : [];
}

export default function AccessRequest() {
  const navigate = useNavigate();

  const { branding } =
    useBranding();

  // ==============================================================
  // CONFIGURAÇÃO PÚBLICA
  // ==============================================================

  const [
    enabled,
    setEnabled,
  ] = useState(true);

  const [
    deadlineHours,
    setDeadlineHours,
  ] = useState(
    DEFAULT_DEADLINE_HOURS,
  );

  const [
    configLoading,
    setConfigLoading,
  ] = useState(true);

  // ==============================================================
  // DADOS DO SOLICITANTE
  // ==============================================================

  const [
    name,
    setName,
  ] = useState("");

  const [
    email,
    setEmail,
  ] = useState("");

  const [
    phone,
    setPhone,
  ] = useState("");

  const [
    password,
    setPassword,
  ] = useState("");

  const [
    confirmPassword,
    setConfirmPassword,
  ] = useState("");

  const [
    showPassword,
    setShowPassword,
  ] = useState(false);

  const [
    showConfirmPassword,
    setShowConfirmPassword,
  ] = useState(false);

  // ==============================================================
  // EMPRESA
  // ==============================================================

  const [
    companySearch,
    setCompanySearch,
  ] = useState("");

  const [
    companies,
    setCompanies,
  ] = useState([]);

  const [
    selectedCompany,
    setSelectedCompany,
  ] = useState(null);

  const [
    selectedUnit,
    setSelectedUnit,
  ] = useState(null);

  const [
    companyLoading,
    setCompanyLoading,
  ] = useState(false);

  const units = useMemo(
    () =>
      getUnits(
        selectedCompany,
      ),
    [selectedCompany],
  );

  // ==============================================================
  // PERFIL
  // ==============================================================

  const [
    requestedRole,
    setRequestedRole,
  ] = useState(
    "technician",
  );

  const [
    message,
    setMessage,
  ] = useState("");

  // ==============================================================
  // ENVIO
  // ==============================================================

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    success,
    setSuccess,
  ] = useState(null);

  // ==============================================================
  // CONFIGURAÇÃO DA SOLICITAÇÃO
  // ==============================================================

  useEffect(() => {
    let cancelled = false;

    async function loadConfig() {
      try {
        const { data } =
          await axios.get(
            CONFIG_ENDPOINT,
          );

        if (cancelled) {
          return;
        }

        setEnabled(
          data?.enabled !== false,
        );

        const hours = Number(
          data?.review_deadline_hours,
        );

        if (
          Number.isFinite(hours) &&
          hours > 0
        ) {
          setDeadlineHours(
            hours,
          );
        }
      } catch {
        if (!cancelled) {
          setEnabled(true);
          setDeadlineHours(
            DEFAULT_DEADLINE_HOURS,
          );
        }
      } finally {
        if (!cancelled) {
          setConfigLoading(
            false,
          );
        }
      }
    }

    loadConfig();

    return () => {
      cancelled = true;
    };
  }, []);

  // ==============================================================
  // PESQUISA DE EMPRESA
  // ==============================================================

  useEffect(() => {
    if (
      selectedCompany ||
      companySearch.trim().length < 2
    ) {
      setCompanies([]);
      return;
    }

    let cancelled = false;

    const timer =
      window.setTimeout(
        async () => {
          setCompanyLoading(
            true,
          );

          try {
            const { data } =
              await axios.get(
                COMPANIES_ENDPOINT,
                {
                  params: {
                    search:
                      companySearch.trim(),
                  },
                },
              );

            if (cancelled) {
              return;
            }

            const items =
              Array.isArray(
                data?.items,
              )
                ? data.items
                : [];

            setCompanies(
              items
                .map(
                  normalizeCompany,
                )
                .filter(
                  (item) =>
                    item?.id &&
                    item?.name,
                ),
            );
          } catch {
            if (!cancelled) {
              setCompanies([]);
            }
          } finally {
            if (!cancelled) {
              setCompanyLoading(
                false,
              );
            }
          }
        },
        350,
      );

    return () => {
      cancelled = true;
      window.clearTimeout(
        timer,
      );
    };
  }, [
    companySearch,
    selectedCompany,
  ]);

  // ==============================================================
  // SELECIONAR EMPRESA
  // ==============================================================

  const selectCompany =
    (company) => {
      setSelectedCompany(
        company,
      );

      setSelectedUnit(
        null,
      );

      setCompanySearch(
        company.name,
      );

      setCompanies([]);
      setError("");
    };

  // ==============================================================
  // VALIDAR
  // ==============================================================

  const validate = () => {
    if (!name.trim()) {
      return "Informe seu nome completo.";
    }

    if (!email.trim()) {
      return "Informe seu e-mail corporativo.";
    }

    if (!password) {
      return "Crie uma senha para o seu acesso.";
    }

    if (password.length < 6) {
      return "A senha deve possuir pelo menos 6 caracteres.";
    }

    if (
      password !==
      confirmPassword
    ) {
      return "A confirmação da senha não confere.";
    }

    if (!selectedCompany?.id) {
      return "Pesquise e selecione a empresa.";
    }

    if (
      units.length > 0 &&
      !selectedUnit?.id
    ) {
      return "Selecione a unidade.";
    }

    if (!requestedRole) {
      return "Selecione o perfil solicitado.";
    }

    return "";
  };

  // ==============================================================
  // ENVIAR
  // ==============================================================

  const submit =
    async (event) => {
      event.preventDefault();

      setError("");
      setSuccess(null);

      const validation =
        validate();

      if (validation) {
        setError(
          validation,
        );
        return;
      }

      setLoading(true);

      try {
        const payload = {
          name:
            name.trim(),

          email:
            email.trim(),

          password,

          phone:
            phone.trim() ||
            null,

          company_id:
            selectedCompany.id,

          unit_id:
            selectedUnit?.id ||
            null,

          requested_role:
            requestedRole,

          message:
            message.trim() ||
            null,
        };

        const { data } =
          await axios.post(
            REQUEST_ENDPOINT,
            payload,
          );

        setSuccess(
          data || {
            ok: true,
          },
        );
      } catch (err) {
        const detail =
          err.response
            ?.data
            ?.detail;

        setError(
          formatApiErrorDetail(
            detail,
          ) ||
            "Não foi possível enviar sua solicitação.",
        );
      } finally {
        setLoading(false);
      }
    };

  // ==============================================================
  // EMPRESA SELECIONADA
  // ==============================================================

  const selectedCompanyUnits =
    units;

  // ==============================================================
  // SUCESSO
  // ==============================================================

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="w-full max-w-lg">

          <div className="rounded-2xl border border-border bg-card shadow-xl overflow-hidden">

            <div className="p-8 text-center space-y-5">

              <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2
                  size={34}
                  className="text-emerald-500"
                />
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">
                  ASTRAL Control Intelligence
                </p>

                <h1 className="font-heading text-2xl font-bold mt-2">
                  Solicitação enviada
                </h1>

                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  Seu pré-cadastro foi recebido
                  e encaminhado para análise
                  administrativa.
                </p>
              </div>

              {success.protocol && (
                <div className="rounded-xl border border-border bg-muted/30 p-4">
                  <p className="text-xs text-muted-foreground">
                    Protocolo
                  </p>

                  <p className="font-mono font-semibold mt-1">
                    {
                      success.protocol
                    }
                  </p>
                </div>
              )}

              <div className="rounded-xl border border-border bg-muted/20 p-4 text-left">

                <div className="flex gap-3">

                  <Clock3
                    size={18}
                    className="text-primary mt-0.5 shrink-0"
                  />

                  <div>

                    <p className="text-sm font-semibold">
                      Prazo para análise
                    </p>

                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                      A solicitação será analisada
                      em até{" "}
                      <strong className="text-foreground">
                        {success.review_deadline_hours ||
                          deadlineHours}{" "}
                        horas
                      </strong>
                      , conforme a
                      parametrização administrativa
                      da plataforma.
                    </p>

                  </div>

                </div>

              </div>

              <div className="rounded-xl border border-border bg-muted/20 p-4 text-left">

                <div className="flex gap-3">

                  <ShieldCheck
                    size={18}
                    className="text-primary mt-0.5 shrink-0"
                  />

                  <div>

                    <p className="text-sm font-semibold">
                      Acompanhe seu e-mail
                    </p>

                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                      A confirmação da solicitação
                      e o resultado da análise serão
                      enviados para o e-mail informado.
                    </p>

                  </div>

                </div>

              </div>

              <Button
                className="w-full"
                onClick={() =>
                  navigate(
                    "/login",
                  )
                }
              >
                Voltar para o login
              </Button>

            </div>

          </div>

        </div>
      </div>
    );
  }

  // ==============================================================
  // SISTEMA DESATIVADO
  // ==============================================================

  if (
    !configLoading &&
    !enabled
  ) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">

        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center">

          <div className="mx-auto h-14 w-14 rounded-full bg-amber-500/10 flex items-center justify-center">
            <AlertCircle
              size={28}
              className="text-amber-500"
            />
          </div>

          <h1 className="font-heading text-xl font-bold mt-5">
            Solicitações indisponíveis
          </h1>

          <p className="text-sm text-muted-foreground mt-2">
            O administrador desativou temporariamente
            as solicitações de novos usuários.
          </p>

          <Button
            variant="outline"
            className="w-full mt-6"
            onClick={() =>
              navigate(
                "/login",
              )
            }
          >
            Voltar para o login
          </Button>

        </div>

      </div>
    );
  }

  // ==============================================================
  // FORMULÁRIO
  // ==============================================================

  return (
    <div className="min-h-screen bg-background">

      {/* ==========================================================
          CABEÇALHO
          ========================================================== */}

      <header className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur-xl">

        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">

          <div className="flex items-center gap-3">

            {branding.logo_main ? (
              <img
                src={
                  branding.logo_main
                }
                alt={
                  branding.login_name
                }
                className="h-9 w-9 rounded-lg object-cover"
              />
            ) : (
              <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
                <UserPlus
                  size={18}
                  className="text-white"
                />
              </div>
            )}

            <div>

              <p className="font-heading font-bold text-sm leading-none">
                {
                  branding.login_name
                }
              </p>

              <p className="text-[9px] text-muted-foreground tracking-[0.18em] uppercase mt-1">
                {
                  branding.system_name
                }
              </p>

            </div>

          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              navigate(
                "/login",
              )
            }
          >
            <ArrowLeft
              size={15}
              className="mr-2"
            />

            Voltar ao login
          </Button>

        </div>

      </header>


      {/* ==========================================================
          CONTEÚDO
          ========================================================== */}

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12">

        <div className="grid lg:grid-cols-[0.75fr_1.25fr] gap-8 items-start">

          {/* ========================================================
              LADO INSTITUCIONAL
              ======================================================== */}

          <section className="lg:sticky lg:top-24 space-y-6">

            <div>

              <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">
                Primeiro acesso
              </p>

              <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">
                Solicite seu acesso
              </h1>

              <p className="text-muted-foreground mt-3 leading-relaxed">
                Preencha seus dados para solicitar
                acesso à plataforma ASTRAL Control
                Intelligence.
              </p>

            </div>


            <div className="rounded-2xl border border-border bg-card p-5 space-y-4">

              <div className="flex items-start gap-3">

                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <ShieldCheck
                    size={18}
                    className="text-primary"
                  />
                </div>

                <div>

                  <p className="text-sm font-semibold">
                    Acesso controlado
                  </p>

                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    O cadastro não libera acesso
                    automaticamente. Um administrador
                    deverá revisar e aprovar sua
                    solicitação.
                  </p>

                </div>

              </div>


              <div className="h-px bg-border" />


              <div className="flex items-start gap-3">

                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Clock3
                    size={18}
                    className="text-primary"
                  />
                </div>

                <div>

                  <p className="text-sm font-semibold">
                    Prazo de análise
                  </p>

                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    Sua solicitação será analisada
                    em até{" "}
                    <strong className="text-foreground">
                      {deadlineHours}{" "}
                      horas
                    </strong>
                    .
                  </p>

                </div>

              </div>


              <div className="h-px bg-border" />


              <div className="flex items-start gap-3">

                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <LockKeyhole
                    size={18}
                    className="text-primary"
                  />
                </div>

                <div>

                  <p className="text-sm font-semibold">
                    Seus dados protegidos
                  </p>

                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    A senha é enviada de forma
                    segura e armazenada somente
                    como hash no servidor.
                  </p>

                </div>

              </div>

            </div>

          </section>


          {/* ========================================================
              FORMULÁRIO
              ======================================================== */}

          <section>

            <form
              onSubmit={submit}
              className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden"
            >

              <div className="p-5 sm:p-7 space-y-6">

                {/* ==================================================
                    DADOS PESSOAIS
                    ================================================== */}

                <div>

                  <h2 className="font-heading font-semibold">
                    Seus dados
                  </h2>

                  <p className="text-xs text-muted-foreground mt-1">
                    Informe os dados que serão utilizados
                    para análise do acesso.
                  </p>

                </div>


                <div className="grid sm:grid-cols-2 gap-4">

                  <div className="space-y-2">

                    <Label htmlFor="access-name">
                      Nome completo *
                    </Label>

                    <Input
                      id="access-name"
                      required
                      value={name}
                      onChange={(e) =>
                        setName(
                          e.target.value,
                        )
                      }
                      placeholder="Seu nome completo"
                    />

                  </div>


                  <div className="space-y-2">

                    <Label htmlFor="access-email">
                      E-mail corporativo *
                    </Label>

                    <Input
                      id="access-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) =>
                        setEmail(
                          e.target.value,
                        )
                      }
                      placeholder="voce@empresa.com"
                    />

                  </div>

                </div>


                <div className="space-y-2">

                  <Label htmlFor="access-phone">
                    Telefone
                  </Label>

                  <Input
                    id="access-phone"
                    value={phone}
                    onChange={(e) =>
                      setPhone(
                        e.target.value,
                      )
                    }
                    placeholder="(00) 00000-0000"
                  />

                </div>


                {/* ==================================================
                    SENHA
                    ================================================== */}

                <div className="grid sm:grid-cols-2 gap-4">

                  <div className="space-y-2">

                    <Label htmlFor="access-password">
                      Criar senha *
                    </Label>

                    <div className="relative">

                      <Input
                        id="access-password"
                        type={
                          showPassword
                            ? "text"
                            : "password"
                        }
                        required
                        minLength={6}
                        value={password}
                        onChange={(e) =>
                          setPassword(
                            e.target.value,
                          )
                        }
                        placeholder="Mínimo de 6 caracteres"
                        className="pr-10"
                      />

                      <button
                        type="button"
                        onClick={() =>
                          setShowPassword(
                            (value) =>
                              !value,
                          )
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label={
                          showPassword
                            ? "Ocultar senha"
                            : "Mostrar senha"
                        }
                      >
                        {showPassword ? (
                          <EyeOff
                            size={16}
                          />
                        ) : (
                          <Eye
                            size={16}
                          />
                        )}
                      </button>

                    </div>

                  </div>


                  <div className="space-y-2">

                    <Label htmlFor="access-confirm-password">
                      Confirmar senha *
                    </Label>

                    <div className="relative">

                      <Input
                        id="access-confirm-password"
                        type={
                          showConfirmPassword
                            ? "text"
                            : "password"
                        }
                        required
                        minLength={6}
                        value={
                          confirmPassword
                        }
                        onChange={(e) =>
                          setConfirmPassword(
                            e.target.value,
                          )
                        }
                        placeholder="Digite a senha novamente"
                        className="pr-10"
                      />

                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(
                            (value) =>
                              !value,
                          )
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label={
                          showConfirmPassword
                            ? "Ocultar confirmação"
                            : "Mostrar confirmação"
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff
                            size={16}
                          />
                        ) : (
                          <Eye
                            size={16}
                          />
                        )}
                      </button>

                    </div>

                  </div>

                </div>


                {/* ==================================================
                    EMPRESA
                    ================================================== */}

                <div className="space-y-2">

                  <Label>
                    Empresa *
                  </Label>


                  {selectedCompany ? (

                    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">

                      <div className="flex items-start gap-3">

                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Building2
                            size={18}
                            className="text-primary"
                          />
                        </div>

                        <div className="flex-1 min-w-0">

                          <p className="font-semibold text-sm">
                            {
                              selectedCompany.name
                            }
                          </p>

                          {selectedCompany.cnpj && (
                            <p className="text-xs text-muted-foreground mt-1">
                              CNPJ:{" "}
                              {
                                selectedCompany.cnpj
                              }
                            </p>
                          )}

                          {(selectedCompany.city ||
                            selectedCompany.state) && (
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">

                              <MapPin
                                size={11}
                              />

                              {
                                selectedCompany.city
                              }

                              {selectedCompany.city &&
                              selectedCompany.state
                                ? " - "
                                : ""}

                              {
                                selectedCompany.state
                              }

                            </p>
                          )}

                        </div>

                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground text-sm"
                          onClick={() => {
                            setSelectedCompany(
                              null,
                            );

                            setSelectedUnit(
                              null,
                            );

                            setCompanySearch(
                              "",
                            );
                          }}
                        >
                          Alterar
                        </button>

                      </div>

                    </div>

                  ) : (

                    <div className="relative">

                      <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                      />

                      <Input
                        value={
                          companySearch
                        }
                        onChange={(e) =>
                          setCompanySearch(
                            e.target.value,
                          )
                        }
                        placeholder="Digite o nome ou CNPJ da empresa"
                        className="pl-9 pr-10"
                      />

                      {companyLoading && (
                        <Loader2
                          size={16}
                          className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground"
                        />
                      )}

                      {companies.length >
                        0 && (
                        <div className="absolute z-40 top-full left-0 right-0 mt-1 rounded-xl border border-border bg-popover shadow-xl overflow-hidden">

                          {companies.map(
                            (
                              company,
                            ) => (
                              <button
                                type="button"
                                key={
                                  company.id
                                }
                                onClick={() =>
                                  selectCompany(
                                    company,
                                  )
                                }
                                className="w-full text-left px-4 py-3 hover:bg-muted border-b border-border last:border-0"
                              >

                                <div className="flex gap-3">

                                  <Building2
                                    size={17}
                                    className="text-primary mt-0.5 shrink-0"
                                  />

                                  <div>

                                    <p className="text-sm font-medium">
                                      {
                                        company.name
                                      }
                                    </p>

                                    {company.cnpj && (
                                      <p className="text-xs text-muted-foreground mt-0.5">
                                        {
                                          company.cnpj
                                        }
                                      </p>
                                    )}

                                  </div>

                                </div>

                              </button>
                            ),
                          )}

                        </div>
                      )}

                    </div>

                  )}

                </div>


                {/* ==================================================
                    UNIDADE
                    ================================================== */}

                {selectedCompany &&
                  selectedCompanyUnits.length >
                    0 && (
                  <div className="space-y-2">

                    <Label htmlFor="access-unit">
                      Unidade *
                    </Label>

                    <select
                      id="access-unit"
                      value={
                        selectedUnit?.id ||
                        ""
                      }
                      onChange={(e) => {

                        const unit =
                          selectedCompanyUnits.find(
                            (item) =>
                              String(
                                item.id,
                              ) ===
                              String(
                                e.target
                                  .value,
                              ),
                          );

                        setSelectedUnit(
                          unit ||
                            null,
                        );

                      }}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >

                      <option value="">
                        Selecione a unidade
                      </option>

                      {selectedCompanyUnits.map(
                        (
                          unit,
                        ) => (
                          <option
                            key={
                              unit.id
                            }
                            value={
                              unit.id
                            }
                          >
                            {
                              unit.name ||
                              unit.unit_name
                            }
                          </option>
                        ),
                      )}

                    </select>

                  </div>
                )}


                {/* ==================================================
                    PERFIL
                    ================================================== */}

                <div className="space-y-2">

                  <Label htmlFor="access-profile">
                    Perfil solicitado *
                  </Label>

                  <select
                    id="access-profile"
                    value={
                      requestedRole
                    }
                    onChange={(e) =>
                      setRequestedRole(
                        e.target.value,
                      )
                    }
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >

                    {REQUESTED_PROFILES.map(
                      (
                        profile,
                      ) => (
                        <option
                          key={
                            profile.value
                          }
                          value={
                            profile.value
                          }
                        >
                          {
                            profile.label
                          }
                        </option>
                      ),
                    )}

                  </select>

                  <p className="text-xs text-muted-foreground">
                    O perfil solicitado será analisado
                    pelo administrador e não garante
                    aprovação.
                  </p>

                </div>


                {/* ==================================================
                    OBSERVAÇÃO
                    ================================================== */}

                <div className="space-y-2">

                  <Label htmlFor="access-message">
                    Observação
                  </Label>

                  <textarea
                    id="access-message"
                    value={message}
                    onChange={(e) =>
                      setMessage(
                        e.target.value,
                      )
                    }
                    rows={4}
                    maxLength={1000}
                    placeholder="Informe alguma informação adicional que possa ajudar na análise."
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                  />

                  <p className="text-[11px] text-muted-foreground text-right">
                    {message.length}/1000
                  </p>

                </div>


                {/* ==================================================
                    ERRO
                    ================================================== */}

                {error && (
                  <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4 flex items-start gap-3">

                    <AlertCircle
                      size={18}
                      className="text-red-500 mt-0.5 shrink-0"
                    />

                    <div>

                      <p className="text-sm font-semibold text-red-500">
                        Não foi possível enviar
                      </p>

                      <p className="text-sm text-red-400 mt-1">
                        {error}
                      </p>

                    </div>

                  </div>
                )}


                {/* ==================================================
                    PRAZO
                    ================================================== */}

                <div className="rounded-xl border border-border bg-muted/20 p-4">

                  <div className="flex items-start gap-3">

                    <Clock3
                      size={18}
                      className="text-primary mt-0.5 shrink-0"
                    />

                    <div>

                      <p className="text-sm font-semibold">
                        Prazo de análise
                      </p>

                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        Após o envio, o administrador
                        terá até{" "}
                        <strong className="text-foreground">
                          {deadlineHours}{" "}
                          horas
                        </strong>{" "}
                        para analisar a solicitação,
                        conforme a configuração da
                        plataforma.
                      </p>

                    </div>

                  </div>

                </div>


                {/* ==================================================
                    BOTÕES
                    ================================================== */}

                <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">

                  <Button
                    type="button"
                    variant="outline"
                    className="sm:flex-1"
                    onClick={() =>
                      navigate(
                        "/login",
                      )
                    }
                    disabled={loading}
                  >
                    Cancelar
                  </Button>

                  <Button
                    type="submit"
                    className="sm:flex-1"
                    disabled={
                      loading ||
                      configLoading
                    }
                  >

                    {loading ? (
                      <>
                        <Loader2
                          size={16}
                          className="mr-2 animate-spin"
                        />

                        Enviando solicitação...
                      </>
                    ) : (
                      <>
                        <UserPlus
                          size={16}
                          className="mr-2"
                        />

                        Enviar solicitação
                      </>
                    )}

                  </Button>

                </div>

              </div>

            </form>

          </section>

        </div>

      </main>

    </div>
  );
}